import "./index.css";

import React from 'react';
import {connect} from 'react-redux';
import DialogComponent, {DIALOG_TYPE} from 'Core/components/DialogComponent';
import {reducerStoreKey} from './reducer';
import {getPageActions} from 'Core/helpers/redux';
import * as actions from './actions';
import {selectors} from 'Core/store/reducers';
import * as filterDataMap from "./dataMap/filter";
import {areAllObjectPropsEmpty, getArray, getString} from 'Core/helpers/data';
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import {BUTTON_STYLE} from 'Core/components/display/Button';
import AccountSelectInput from '../../../../../../components/input/AccountSelectInput';
import Label from 'Core/components/display/Label';
import * as pageConfig from '../../config';
import DataTable, {DATA_TABLE_CELL_TYPE} from 'Core/components/advanced/DataTable';
import {PAGINATION_TYPE} from 'Core/components/action/Pagination';
import EFaxStatusSelectInput from '../../../../../../components/input/EFaxStatusSelectInput';
import {
	DataTableCellAnyTypeOptionsDataObject,
	DataTableCellDateTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import {E_FAX_LOG_STATUS_ICON, E_FAX_LOG_STATUS_TAG_CLASS} from './const';
import {getAppLocalDatetimeSeparator, getAppLocaleDateFormat, getAppLocaleTimeFormat} from 'Core/helpers/locale';
import {LOCALE_DATE_FORMAT_NAME, LOCALE_TIME_FORMAT_NAME} from 'Core/const/locale';
import {STANDARD_DATE_TIME_FORMAT} from 'Core/const/datetime';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in a component.
 */
const mapStateToProps = state => ({
	mainList: selectors[reducerStoreKey].getEFaxLogList(state),
	mainListPagination: selectors[reducerStoreKey].getEFaxLogListPagination(state),
	mainListSort: selectors[reducerStoreKey].getEFaxLogListSort(state),
	mainListFilter: selectors[reducerStoreKey].getEFaxLogListFilter(state),
});

/**
 * Define all actions that can be used by this component.
 */
const allActions = getPageActions(actions);

class EFaxLogDialog extends DialogComponent {
	constructor(props) {
		super(props, {
			dialogType: DIALOG_TYPE.MESSAGE,
			translationPath: `${pageConfig.translationPath}.dialogs.EFaxLogDialog`,
		});
		
		// Initialize local state
		this.state = {
			hideAccountFilter: false,
		};

		// Data methods
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);
	}
	
	/** @inheritDoc */
	async asyncComponentDidMount() {
		await super.asyncComponentDidMount();
		
		await this.reloadMainList();
	}

	componentWillUnmount() {
		super.componentWillUnmount();
		
		// Clear main table selection
		const {clearEFaxLogListAction} = this.props;
		clearEFaxLogListAction();
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Reload the main list using current options (page, sort, ...)
	 * @return {Promise<*>}
	 */
	reloadMainList() {
		const {loadEFaxLogListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(loadEFaxLogListAction, oFilter, pageNo, perPage, sortBy, sortDir)
			.then(() => this.mainListFilterRef?.reload());
	}

	/**
	 * Reload the main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {loadEFaxLogListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(loadEFaxLogListAction, oFilter, pageNo, perPage, sortBy, sortDir);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {loadEFaxLogListAction, mainListPagination, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(loadEFaxLogListAction, oFilter, pageNo, perPage, sortBy, sortDir);
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {loadEFaxLogListAction, mainListPagination, mainListSort} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(filter);

		// Clear main table selection
		if (this.mainListRef) this.mainListRef.clearSelection();

		return this.executeAbortableAction(loadEFaxLogListAction, oFilter, 1, perPage, sortBy, sortDir);
	}

	/**
	 * Remove the main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if the main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	render() {
		const {mainList, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {hideAccountFilter} = this.state;
		
		return this.renderDialog(
			null,
			(
				<div className="dialog-content-wrapper">
					<SimpleStaticSearch
						className="main-search compact-style rows-3"
						defaultCollapse={false}
						collapsable={false}
						layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
						buttonProps={{
							displayStyle: BUTTON_STYLE.DEFAULT
						}}
						options={[
							new SimpleStaticSearchOptionObject(
								'accountId',
								this.t('accountIdField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
								{
									isClearable: true,
									primaryKey: 'id',
									getOptionValue: o => o.id,
									onLoaded: accounts => this.setState({hideAccountFilter: (getArray(accounts).length === 1)}),
								},
								AccountSelectInput,
								{
									className: (hideAccountFilter ? 'no-display' : ''),
								}
							),
							new SimpleStaticSearchOptionObject(
								'status',
								this.t('statusField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
								{},
								EFaxStatusSelectInput,
							),
							new SimpleStaticSearchOptionObject(
								'faxNumber',
								this.t('faxNumberField'),
							),
							new SimpleStaticSearchOptionObject(
								'userName',
								this.t('usernameField')
							),
							new SimpleStaticSearchOptionObject(
								'dateFrom',
								this.t('dateFromField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
								{
									valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
								}
							),
							new SimpleStaticSearchOptionObject(
								'dateTo',
								this.t('dateToField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
								{
									valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
								}
							),
						]}
						value={mainListFilter}
						title={(<Label icon="fax" content={this.t('title')} />)}
						applied={!this.isMainListFilterEmpty()}
						enableToolbar={true}
						enableResetButton={false}
						onChange={this.filterMainList}
						onRemove={this.removeMainListFilter}
						ref={node => { this.mainListFilterRef = node; }}
					/>
					
					<DataTable
						className="main-page-table standard"
						highlightOnHover={true}
						columns={[
							{
								name: 'status',
								sortName: 'status',
								label: this.t('statusField'),
								dataType: DATA_TABLE_CELL_TYPE.ANY,
								dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
									content: row => (
										<div className="text-right">
											<Label
												element="span"
												elementProps={{
													className: `tag no-wrap ${getString(E_FAX_LOG_STATUS_TAG_CLASS, row.status)}`,
												}}
												icon={getString(E_FAX_LOG_STATUS_ICON, row.status)}
												content={this.t(row.status, 'constants.EFaxLogStatus')}
											/>
										</div>
									),
								}),
								defaultValue: '—',
								width: 1,
							},
							{
								name: 'reportName',
								sortName: 'reportName',
								label: this.t('reportNameField'),
								dataType: DATA_TABLE_CELL_TYPE.ANY,
								dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
									content: row => (
										<Label
											element="span"
											elementProps={{
												className: 'no-wrap',
												title: getString(row, 'reportName'),
											}}
											content={getString(row, 'reportName')}
										/>
									),
								}),
								width: 1,
								defaultValue: '—',
							},
							{
								name: 'faxNumber',
								sortName: 'faxNumber',
								label: this.t('faxNumberField'),
								defaultValue: '—',
								width: 1,
							},
							{
								name: 'username',
								label: this.t('usernameField'),
								defaultValue: '—',
							},
							{
								name: 'creationDate',
								sortName: 'creationDate',
								label: this.t('creationDateField'),
								dataType: DATA_TABLE_CELL_TYPE.DATE,
								dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
									outputFormat: [
										getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.STANDARD),
										getAppLocalDatetimeSeparator(),
										getAppLocaleTimeFormat(LOCALE_TIME_FORMAT_NAME.STANDARD),
									],
									whiteSpace: 'nowrap',
								}),
								width: 1,
								defaultValue: '—',
							},
							{
								name: 'deliveredDate',
								label: this.t('deliveredDateField'),
								dataType: DATA_TABLE_CELL_TYPE.DATE,
								dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
									outputFormat: [
										getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.STANDARD),
										getAppLocalDatetimeSeparator(),
										getAppLocaleTimeFormat(LOCALE_TIME_FORMAT_NAME.STANDARD),
									],
									whiteSpace: 'nowrap',
								}),
								width: 1,
								defaultValue: '—',
							},
						]}
						data={mainList}
						paginationType={PAGINATION_TYPE.DYNAMIC}
						onSortByColumn={this.sortMainList}
						onPaginationClick={this.loadMainListPage}
						{...mainListPagination}
						{...mainListSort}
					/>
				</div>
			)
		);
	}
}

export default connect(mapStateToProps, allActions, null, {forwardRef: true})(EFaxLogDialog);